import { useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { store } from "aldoo-ra/Store"
import PlanCard from "./PlanCard"
import SeatOptionsModal from "./SeatOptionsModal"

const LIFETIME_PREFIX = "snap-lifetime-"

/**
 * Resolve the lifetime license tiers (1/2/5/10 Macs) from the public
 * pricing payload. Returns [{product, storePrice, model, amount, seats}]
 * sorted by seat count.
 */
export function getLifetimeTiers(storeData) {
  const tiers = []
  for (const product of Object.values(storeData.products || {})) {
    if (!product.pid?.startsWith(LIFETIME_PREFIX)) continue
    const priceId = product.price?._id || product.price
    const storePrice = storeData.prices?.[String(priceId)]
    const model = storePrice?.price_model?.find((m) => m.primary) || storePrice?.price_model?.[0]
    if (!model) continue
    const seats = product.seats || parseInt(product.pid.slice(LIFETIME_PREFIX.length)) || 1
    tiers.push({ product, storePrice, model, amount: model.amount, seats })
  }
  return tiers.sort((a, b) => a.seats - b.seats)
}

export default function OneTimeCard({ storeData, featuresByPid = {} }) {
  const texts = TextBundle({ id: "pricing" })
  const [seatModalOpen, setSeatModalOpen] = useState(false)
  const tiers = getLifetimeTiers(storeData)
  const baseTier = tiers[0]

  const checkout = (tier) => {
    setSeatModalOpen(false)
    const { product, storePrice, model } = tier
    const webEntry = model.real_product_id?.find((r) => {
      const platform = typeof r.platform === "object" ? r.platform?.value : r.platform
      return platform === "Web"
    })
    store.addToCart({
      product: {
        pid: product.pid,
        product_id: storePrice._id,
        store_id: webEntry?.id || null,
        name: product.name,
        description: product.description,
        currency: model.currency || "USD",
        price_model: [{ ...model, primary: true }],
        duration: "one-time",
        thumbnail: product.thumbnail,
      },
      proceedToCart: true,
    })
  }

  // Feature Matrix bullets for the base (1-Mac) tier — the card shows one
  // fixed set of highlights regardless of which tier is picked in the modal.
  // Each bullet is { name, value } — matrix rows split "Max link duration"
  // (name) from "3 days" (value, bolded).
  const highlights = featuresByPid[baseTier?.product?.pid] || []

  return (
    <PlanCard
      className="paper_70"
      title={texts?.onetime_title}
      subtitle={texts?.onetime_subtitle}
      price={
        baseTier ? (
          <>
            <Typography component="span" variant="h2">
              ${baseTier.amount}
            </Typography>
            <span className="text-text_secondary text-sm ml-1">
              {texts?.onetime_price_unit}
            </span>
            <Typography variant="txt2_dim" className="block mt-1">
              {texts?.onetime_seat_note}
            </Typography>
          </>
        ) : (
          <Typography variant="txt2_dim">
            {texts?.pricing_unavailable}
          </Typography>
        )
      }
      button={
        <Button
          variant="primary"
          className="w-full mb-6"
          disabled={!baseTier}
          onClick={() => setSeatModalOpen(true)}
        >
          {texts?.continue_button}
        </Button>
      }
      highlights={highlights}
    >
      <SeatOptionsModal
        isOpen={seatModalOpen}
        onClose={() => setSeatModalOpen(false)}
        tiers={tiers}
        onSelect={checkout}
      />
    </PlanCard>
  )
}
